import React, { useState } from "react";
import { NavLink, Link, Outlet, Navigate, useNavigate } from "react-router-dom";
import {
  LayoutGrid, ShoppingBag, MessageSquare, Users, Hammer, Images, PenSquare,
  LogOut, Menu, X, ExternalLink, Printer, BarChart3, Settings as SettingsIcon,
  Flower2, BookOpen, PackageCheck, Boxes, Fingerprint, Ticket, Store,
} from "lucide-react";
import { LOGOS } from "../../lib/brand";
import { useAuth } from "../../context/AuthContext";

const NAV_GROUPS = [
  {
    label: null,
    items: [{ to: "/console", end: true, label: "DASHBOARD", Icon: LayoutGrid }],
  },
  {
    label: "THE ETCHERY",
    dot: "#C39EE3",
    items: [
      { to: "/console/orders", label: "ORDERS", Icon: ShoppingBag },
      { to: "/console/supply", label: "SUPPLY ROOM", Icon: Store },
      { to: "/console/print-queue", label: "PRINT QUEUE", Icon: Printer },
      { to: "/console/workshop", label: "WORKSHOP MANAGER", Icon: Hammer },
    ],
  },
  {
    label: "HERITAGE DAHLIAS",
    dot: "#7ED9A7",
    items: [
      { to: "/console/heritage-orders", label: "HERITAGE ORDERS", Icon: Flower2 },
      { to: "/console/varieties", label: "VARIETY DATABASE", Icon: BookOpen },
    ],
  },
  {
    label: "SHARED ADMIN",
    dot: "#FFD23F",
    items: [
      { to: "/console/packing", label: "PACKING & SHIPPING", Icon: PackageCheck },
      { to: "/console/inventory", label: "INVENTORY", Icon: Boxes },
      { to: "/console/enquiries", label: "ENQUIRIES", Icon: MessageSquare },
      { to: "/console/bloom-id", label: "BLOOM ID", Icon: Fingerprint },
      { to: "/console/vouchers", label: "VOUCHERS", Icon: Ticket },
      { to: "/console/customers", label: "CUSTOMERS", Icon: Users },
      { to: "/console/reports", label: "REPORTS", Icon: BarChart3 },
      { to: "/console/gallery", label: "GALLERY MANAGER", Icon: Images },
      { to: "/console/editor", label: "SITE EDITOR", Icon: PenSquare },
      { to: "/console/settings", label: "SETTINGS", Icon: SettingsIcon },
    ],
  },
];

const SidebarNav = ({ onNavigate }) => (
  <div className="space-y-5">
    {NAV_GROUPS.map((group) => (
      <div key={group.label || "root"}>
        {group.label && (
          <p className="flex items-center gap-2 px-4 mb-1.5 text-[10px] uppercase tracking-[0.26em] text-cream/50 font-bold" data-testid={`console-group-${group.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: group.dot }} />
            {group.label}
          </p>
        )}
        <ul className="space-y-1">
          {group.items.map(({ to, label, Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                onClick={onNavigate}
                data-testid={`console-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2 rounded-full text-sm transition-colors ${
                    isActive ? "bg-cream text-magenta font-semibold" : "text-cream/80 hover:bg-cream/10"
                  }`
                }
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="font-serif-display uppercase tracking-[0.08em]">{label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

const ConsoleLayout = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  if (user === null) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-plum-deep/60">Checking your session…</div>;
  }
  if (user === false) return <Navigate to="/console/login" replace />;

  const doLogout = async () => {
    await logout();
    nav("/console/login");
  };

  return (
    <div className="min-h-screen bg-cream flex">
      <aside data-testid="console-sidebar" className="hidden lg:flex w-[280px] shrink-0 bg-plum text-cream flex-col sticky top-0 h-screen">
        <Link to="/console" className="flex items-center gap-3 px-7 pt-7 pb-6 border-b border-cream/10">
          <div className="w-12 h-12 rounded-full bg-cream flex items-center justify-center overflow-hidden shrink-0">
            <img src={LOGOS.bloomFoundry} alt="" className="w-[80%] h-[80%] object-contain" />
          </div>
          <div>
            <p className="font-serif-display uppercase text-[10.5px] tracking-[0.28em] text-cream/60">FOUNDRY</p>
            <p className="font-serif-display uppercase text-2xl leading-none">CONSOLE</p>
          </div>
        </Link>
        <nav className="flex-1 overflow-y-auto py-6 px-4">
          <SidebarNav />
        </nav>
        <div className="px-5 py-5 border-t border-cream/10 space-y-3">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-cream/70 text-xs uppercase tracking-[0.18em] hover:text-cream" data-testid="console-view-site">
            <ExternalLink className="w-3.5 h-3.5" /> VIEW SITE
          </a>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange flex items-center justify-center text-cream font-bold shrink-0">
              {(user.name || "A")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate">{user.name || "Admin"}</p>
              <p className="text-[11px] text-cream/60 truncate">{user.email}</p>
            </div>
            <button onClick={doLogout} className="p-2 rounded-full hover:bg-cream/10" data-testid="console-logout" aria-label="Log out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        {/* Mobile topbar */}
        <header className="lg:hidden sticky top-0 z-40 bg-plum text-cream px-4 py-3 flex items-center justify-between">
          <Link to="/console" className="font-serif-display uppercase text-lg tracking-[0.1em]">FOUNDRY CONSOLE</Link>
          <button onClick={() => setOpen((v) => !v)} data-testid="console-mobile-toggle" aria-label="Toggle console menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </header>
        {open && (
          <div className="lg:hidden bg-plum text-cream px-4 pb-5" data-testid="console-mobile-menu">
            <SidebarNav onNavigate={() => setOpen(false)} />
            <button onClick={doLogout} className="mt-4 flex items-center gap-2 text-cream/80 text-sm uppercase tracking-[0.14em]" data-testid="console-mobile-logout">
              <LogOut className="w-4 h-4" /> LOG OUT
            </button>
          </div>
        )}

        <div className="px-4 md:px-10 py-8 md:py-10">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default ConsoleLayout;
